import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { adminAPI } from '../lib/api';
import { Navbar } from '../components/Navbar';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  Users, FolderOpen, MessageSquare, Calendar, TrendingUp, Flag, Loader2
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await adminAPI.getStats();
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch admin stats:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-dashboard">
      <Navbar />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-slate-900">Admin Dashboard</h1>
          <p className="text-slate-600">Manage your PDX Hacks community</p>
        </div>

        {/* Stats */}
        {stats && (
          <>
            <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
              <Card>
                <CardContent className="flex items-center gap-4 p-5">
                  <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                    <Users className="w-5 h-5 text-indigo-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">{stats.totals?.users || 0}</p>
                    <p className="text-sm text-slate-600">Users</p>
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="flex items-center gap-4 p-5">
                  <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                    <FolderOpen className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">{stats.totals?.projects || 0}</p>
                    <p className="text-sm text-slate-600">Projects</p>
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="flex items-center gap-4 p-5">
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                    <MessageSquare className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">{stats.totals?.threads || 0}</p>
                    <p className="text-sm text-slate-600">Threads</p>
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="flex items-center gap-4 p-5">
                  <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                    <Calendar className="w-5 h-5 text-orange-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">{stats.totals?.events || 0}</p>
                    <p className="text-sm text-slate-600">Events</p>
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="flex items-center gap-4 p-5">
                  <div className="w-10 h-10 bg-cyan-100 rounded-lg flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-cyan-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">{stats.totals?.comments || 0}</p>
                    <p className="text-sm text-slate-600">Comments</p>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Recent Activity */}
            <div className="grid lg:grid-cols-3 gap-6">
              {/* Recent Users */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Users className="w-5 h-5 text-indigo-600" />
                    Recent Users
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {stats.recent?.users?.length > 0 ? (
                    <div className="space-y-3">
                      {stats.recent.users.map((user) => (
                        <div key={user.user_id} className="flex items-center justify-between">
                          <div>
                            <Link to={`/users/${user.user_id}`} className="font-medium text-slate-900 hover:text-indigo-600">
                              {user.name}
                            </Link>
                            <p className="text-xs text-slate-500">{user.email}</p>
                          </div>
                          <Badge variant="secondary" className="text-xs">
                            {user.role}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-slate-500 text-sm">No recent users</p>
                  )}
                </CardContent>
              </Card>

              {/* Recent Projects */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <FolderOpen className="w-5 h-5 text-emerald-600" />
                    Recent Projects
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {stats.recent?.projects?.length > 0 ? (
                    <div className="space-y-3">
                      {stats.recent.projects.map((project) => (
                        <div key={project.project_id}>
                          <Link to={`/projects/${project.project_id}`} className="font-medium text-slate-900 hover:text-indigo-600">
                            {project.title}
                          </Link>
                          <p className="text-xs text-slate-500">
                            by {project.author_name} • {formatDistanceToNow(new Date(project.created_at), { addSuffix: true })}
                          </p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-slate-500 text-sm">No recent projects</p>
                  )}
                </CardContent>
              </Card>

              {/* Recent Threads */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <MessageSquare className="w-5 h-5 text-purple-600" />
                    Recent Threads
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {stats.recent?.threads?.length > 0 ? (
                    <div className="space-y-3">
                      {stats.recent.threads.map((thread) => (
                        <div key={thread.thread_id}>
                          <Link to={`/forum/thread/${thread.thread_id}`} className="font-medium text-slate-900 hover:text-indigo-600">
                            {thread.title}
                          </Link>
                          <p className="text-xs text-slate-500">
                            by {thread.author_name} • {formatDistanceToNow(new Date(thread.created_at), { addSuffix: true })}
                          </p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-slate-500 text-sm">No recent threads</p>
                  )}
                </CardContent>
              </Card>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
